import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (token && token !== 'null') {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface ChatConversation {
  _id: string;
  user: string;
  title: string;
  messages: ChatMessage[];
  llmProvider: string;
  llmModel: string;
  isArchived: boolean;
  lastMessageAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Get all conversations for current user
 */
export const getConversations = async (params: {
  archived?: boolean;
  limit?: number;
  skip?: number;
} = {}): Promise<{
  success: boolean;
  data?: ChatConversation[];
  total?: number;
  message?: string;
}> => {
  try {
    const { archived = false, limit = 50, skip = 0 } = params;

    console.log('Fetching conversations with params:', { archived, limit, skip });
    const headers = getAuthHeaders();
    console.log('Auth headers:', headers);

    const response = await axios.get(`${API_BASE_URL}/chat/conversations`, {
      params: { archived, limit, skip },
      headers
    });

    console.log('API response status:', response.status);
    console.log('API response data:', response.data);

    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch conversations:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    return {
      success: false,
      message: error.response?.data?.message || '获取对话历史失败'
    };
  }
};

/**
 * Get a specific conversation with all messages
 */
export const getConversation = async (conversationId: string): Promise<{
  success: boolean;
  data?: ChatConversation;
  message?: string;
}> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/chat/conversations/${conversationId}`, {
      headers: getAuthHeaders()
    });

    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch conversation:', error);
    return {
      success: false,
      message: error.response?.data?.message || '获取对话详情失败'
    };
  }
};

/**
 * Create a new conversation
 */
export const createConversation = async (data: {
  title?: string;
  llmProvider?: string;
  llmModel?: string;
} = {}): Promise<{
  success: boolean;
  data?: ChatConversation;
  message?: string;
}> => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/chat/conversations`,
      data,
      {
        headers: getAuthHeaders()
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Failed to create conversation:', error);
    return {
      success: false,
      message: error.response?.data?.message || '创建对话失败'
    };
  }
};

/**
 * Add a message to a conversation
 */
export const addMessage = async (
  conversationId: string,
  role: 'user' | 'assistant',
  content: string
): Promise<{
  success: boolean;
  data?: ChatConversation;
  message?: string;
}> => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/chat/conversations/${conversationId}/messages`,
      { role, content },
      {
        headers: getAuthHeaders()
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Failed to add message:', error);
    return {
      success: false,
      message: error.response?.data?.message || '添加消息失败'
    };
  }
};

/**
 * Update conversation title
 */
export const updateConversationTitle = async (
  conversationId: string,
  title: string
): Promise<{
  success: boolean;
  data?: ChatConversation;
  message?: string;
}> => {
  try {
    console.log('API: Updating conversation title:', conversationId, 'to:', title);
    const headers = getAuthHeaders();
    console.log('API: Request headers:', headers);

    const response = await axios.patch(
      `${API_BASE_URL}/chat/conversations/${conversationId}/title`,
      { title },
      {
        headers
      }
    );

    console.log('API: Update title response:', response.status, response.data);
    return response.data;
  } catch (error: any) {
    console.error('API: Failed to update title:', error);
    console.error('API: Error response:', error.response?.data);
    console.error('API: Error status:', error.response?.status);
    return {
      success: false,
      message: error.response?.data?.message || '更新标题失败'
    };
  }
};

/**
 * Archive/unarchive a conversation
 */
export const archiveConversation = async (
  conversationId: string,
  isArchived: boolean
): Promise<{
  success: boolean;
  data?: ChatConversation;
  message?: string;
}> => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/chat/conversations/${conversationId}/archive`,
      { isArchived },
      {
        headers: getAuthHeaders()
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Failed to archive conversation:', error);
    return {
      success: false,
      message: error.response?.data?.message || '归档对话失败'
    };
  }
};

/**
 * Delete a conversation
 */
export const deleteConversation = async (
  conversationId: string
): Promise<{
  success: boolean;
  message?: string;
}> => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/chat/conversations/${conversationId}`, {
      headers: getAuthHeaders()
    });

    return response.data;
  } catch (error: any) {
    console.error('Failed to delete conversation:', error);
    return {
      success: false,
      message: error.response?.data?.message || '删除对话失败'
    };
  }
};

/**
 * Delete all conversations for user
 */
export const deleteAllConversations = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/chat/conversations`, {
      headers: getAuthHeaders()
    });

    return response.data;
  } catch (error: any) {
    console.error('Failed to delete all conversations:', error);
    return {
      success: false,
      message: error.response?.data?.message || '删除所有对话失败'
    };
  }
};
